// ─────────────────────────────────────────────────────────────────
// Glass-i — Thickness Master page (Stage 6B.8A-3K.1+).
//
// Compact spreadsheet-style master — same density as Process Master
// + Production Departments. Reuses .pd-shell / .pd-toolbar / .pd-card
// / .pd-grid / .pd-pill from the Production Departments stylesheet
// so all three master pages share one visual rhythm.
//
// Add Thickness opens a small modal; row actions: ✎ Edit (inline),
// ✓ Save / ✕ Cancel during edit, ▲▼ reorder, × archive.
// ─────────────────────────────────────────────────────────────────

import { useEffect, useState, useCallback } from 'react';
import {
  Button, Input, InputNumber, Switch, Table, Tooltip, Space,
  Modal, Form, Popconfirm, message, Spin
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, SaveOutlined,
  CloseOutlined, ArrowUpOutlined, ArrowDownOutlined, ReloadOutlined,
  BlockOutlined, SearchOutlined
} from '@ant-design/icons';
import { thicknessMasterApi } from './thicknessMasterApi';
import '../productionDepartments/ProductionDepartmentsPage.css';

function defaultLabel(mm) {
  if (mm === '' || mm === null || mm === undefined) return '';
  const n = Number(mm);
  if (!Number.isFinite(n)) return '';
  return `${n}mm`;
}

export default function ThicknessMasterPage() {
  const [rows,        setRows]        = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [search,      setSearch]      = useState('');
  const [creating,    setCreating]    = useState(false);
  const [editingId,   setEditingId]   = useState(null);
  const [editValues,  setEditValues]  = useState({});
  const [createForm]                  = Form.useForm();

  // ── Load ─────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const out = await thicknessMasterApi.list({ limit: 500 });
      setRows((out && out.rows) || []);
    } catch (e) {
      message.error(e?.response?.data?.error?.message
        || e.message || 'Failed to load thicknesses.');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // ── Create ───────────────────────────────────────────────────
  const onCreate = async () => {
    let values;
    try { values = await createForm.validateFields(); } catch (_e) { return; }
    try {
      await thicknessMasterApi.create({
        thicknessMm:    Number(values.thicknessMm),
        thicknessLabel: values.thicknessLabel
                         || defaultLabel(values.thicknessMm),
        sortOrder:      values.sortOrder != null ? Number(values.sortOrder) : 0,
        isActive:       values.isActive !== false,
        remarks:        values.remarks || null
      });
      message.success('Thickness added.');
      setCreating(false);
      createForm.resetFields();
      load();
    } catch (e) {
      message.error(e?.response?.data?.error?.message
        || 'Failed to add thickness.');
    }
  };

  // ── Inline edit ──────────────────────────────────────────────
  const startEdit = (row) => {
    setEditingId(row.id);
    setEditValues({
      thicknessMm:    row.thicknessMm,
      thicknessLabel: row.thicknessLabel,
      sortOrder:      row.sortOrder,
      isActive:       row.isActive,
      remarks:        row.remarks || ''
    });
  };
  const cancelEdit = () => { setEditingId(null); setEditValues({}); };
  const saveEdit = async (row) => {
    if (!(Number(editValues.thicknessMm) > 0)) {
      message.error('Thickness must be greater than zero.');
      return;
    }
    try {
      await thicknessMasterApi.update(row.id, {
        thicknessMm:    Number(editValues.thicknessMm),
        thicknessLabel: editValues.thicknessLabel
                         || defaultLabel(editValues.thicknessMm),
        sortOrder:      Number(editValues.sortOrder || 0),
        isActive:       editValues.isActive !== false,
        remarks:        editValues.remarks || null
      });
      message.success('Saved.');
      cancelEdit();
      load();
    } catch (e) {
      message.error(e?.response?.data?.error?.message || 'Failed to save.');
    }
  };

  const onDelete = async (row) => {
    try {
      await thicknessMasterApi.remove(row.id);
      message.success(`Archived ${row.thicknessLabel}.`);
      load();
    } catch (e) {
      message.error(e?.response?.data?.error?.message || 'Failed to archive.');
    }
  };

  const move = async (row, direction) => {
    const idx = rows.findIndex((r) => r.id === row.id);
    if (idx < 0) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= rows.length) return;
    const other = rows[swapIdx];
    try {
      await thicknessMasterApi.reorder({
        orders: [
          { id: row.id,   sortOrder: other.sortOrder },
          { id: other.id, sortOrder: row.sortOrder }
        ]
      });
      load();
    } catch (e) {
      message.error(e?.response?.data?.error?.message || 'Failed to reorder.');
    }
  };

  // ── Filter ───────────────────────────────────────────────────
  const visible = (() => {
    const needle = (search || '').trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) =>
      [r.thicknessLabel, String(r.thicknessMm), r.remarks]
        .some((v) => v && String(v).toLowerCase().includes(needle)));
  })();

  // ── Columns ──────────────────────────────────────────────────
  const columns = [
    { title: '#', key: '__sno', width: 38, align: 'center',
      render: (_v, _r, i) => <span style={{ color: '#94a3b8' }}>{i + 1}</span> },
    { title: 'Thickness (mm)', dataIndex: 'thicknessMm', width: 140, align: 'right',
      render: (v, row) => editingId === row.id
        ? <InputNumber size="small" min={0.1} step={0.5}
            value={editValues.thicknessMm}
            onChange={(n) => setEditValues((s) => ({ ...s, thicknessMm: n }))}
            style={{ width: '100%' }} />
        : <span className="pd-num" style={{ fontWeight: 600, color: '#0f172a' }}>{v}</span> },
    { title: 'Label', dataIndex: 'thicknessLabel', width: 180,
      render: (v, row) => editingId === row.id
        ? <Input size="small" value={editValues.thicknessLabel}
            placeholder={defaultLabel(editValues.thicknessMm)}
            onChange={(e) => setEditValues((s) => ({ ...s, thicknessLabel: e.target.value }))} />
        : <span className="pd-code" style={{ color: '#475569' }}>{v}</span> },
    { title: 'Sort', dataIndex: 'sortOrder', width: 80, align: 'right',
      render: (v, row) => editingId === row.id
        ? <InputNumber size="small" min={0} step={10}
            value={editValues.sortOrder}
            onChange={(n) => setEditValues((s) => ({ ...s, sortOrder: n == null ? 0 : n }))}
            style={{ width: '100%' }} />
        : <span className="pd-num">{v}</span> },
    { title: 'Active', dataIndex: 'isActive', width: 80, align: 'center',
      render: (v, row) => editingId === row.id
        ? <Switch size="small" checked={editValues.isActive !== false}
            onChange={(c) => setEditValues((s) => ({ ...s, isActive: c }))} />
        : <span className={`pd-pill ${v ? 'pd-pill--active' : 'pd-pill--inactive'}`}>
            {v ? 'Active' : 'Inactive'}
          </span> },
    { title: 'Remarks', dataIndex: 'remarks',
      render: (v, row) => editingId === row.id
        ? <Input size="small" value={editValues.remarks}
            onChange={(e) => setEditValues((s) => ({ ...s, remarks: e.target.value }))} />
        : (v ? <span style={{ color: '#475569' }}>{v}</span>
             : <span className="pd-empty-cell">—</span>) },
    { title: 'Actions', key: '__actions', width: 140, align: 'right', fixed: 'right',
      className: 'pd-actions',
      render: (_v, row, idx) => editingId === row.id ? (
        <Space size={0}>
          <Tooltip title="Save"><Button size="small" type="primary"
            icon={<SaveOutlined />} onClick={() => saveEdit(row)} /></Tooltip>
          <Tooltip title="Cancel"><Button size="small" type="text"
            icon={<CloseOutlined />} onClick={cancelEdit} /></Tooltip>
        </Space>
      ) : (
        <Space size={0}>
          <Tooltip title="Move up"><Button size="small" type="text"
            icon={<ArrowUpOutlined />} disabled={idx === 0}
            onClick={() => move(row, 'up')} /></Tooltip>
          <Tooltip title="Move down"><Button size="small" type="text"
            icon={<ArrowDownOutlined />} disabled={idx === rows.length - 1}
            onClick={() => move(row, 'down')} /></Tooltip>
          <Tooltip title="Edit"><Button size="small" type="text"
            icon={<EditOutlined />} onClick={() => startEdit(row)} /></Tooltip>
          <Popconfirm title="Archive this thickness?" okType="danger" okText="Archive"
            onConfirm={() => onDelete(row)}>
            <Tooltip title="Archive"><Button size="small" type="text" danger
              icon={<DeleteOutlined />} /></Tooltip>
          </Popconfirm>
        </Space>
      ) }
  ];

  const activeCount = rows.filter((r) => r.isActive).length;

  return (
    <div className="pd-shell">
      {/* Header */}
      <div className="pd-header">
        <div>
          <div className="pd-header__title">
            <span className="pd-header__icon"><BlockOutlined /></span>
            Thickness Master
          </div>
          <div className="pd-header__subtitle">
            Shared glass thickness list. Consumed by Process Master, Glass Concepts,
            Proforma, Production and Inventory.
          </div>
        </div>
        <Space size={6}>
          <span className="pd-stat-chip"><strong>{activeCount}</strong> active</span>
          <span className="pd-stat-chip"><strong>{rows.length}</strong> total</span>
        </Space>
      </div>

      {/* Toolbar */}
      <div className="pd-toolbar">
        <Input allowClear size="small"
          prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
          placeholder="Search thickness, label, remarks…"
          value={search} onChange={(e) => setSearch(e.target.value)}
          style={{ width: 260 }} />

        <div className="pd-toolbar__divider" />
        <span style={{ color: '#94a3b8', fontSize: 11.5 }}>
          {visible.length} shown
        </span>

        <div className="pd-toolbar__spacer" />

        <Tooltip title="Reload">
          <Button size="small" icon={<ReloadOutlined />} onClick={load} />
        </Tooltip>
        <Button size="small" type="primary" icon={<PlusOutlined />}
          onClick={() => setCreating(true)}>
          Add Thickness
        </Button>
      </div>

      {/* Table */}
      <div className="pd-card">
        <Spin spinning={loading}>
          <Table className="pd-grid" size="small" rowKey="id"
            dataSource={visible} columns={columns}
            pagination={false} sticky
            scroll={{ x: 'max-content' }}
            rowClassName={(r) => r.isActive ? '' : 'pd-inactive-row'}
            locale={{
              emptyText: rows.length === 0
                ? 'No thicknesses yet. Click Add Thickness to start.'
                : 'No thicknesses match the current search.'
            }} />
        </Spin>
      </div>

      {/* Create modal — uses default AntD styling. The previous
          `erp-prod-dept-modal` + `erp-prod-dept-form` classes were
          designed for the Production Department drawer-as-modal,
          which restores body padding via `styles.body`. Without
          that prop the body collapses to zero padding AND the
          .ant-input-number wrapper gets force-sized, jamming the
          handler buttons so typed values weren't committing. */}
      <Modal
        open={creating}
        title="Add Thickness"
        okText="Add"
        onCancel={() => { setCreating(false); createForm.resetFields(); }}
        onOk={onCreate}
        destroyOnClose
        width={420}
        centered
      >
        <Form form={createForm} layout="vertical" requiredMark="optional"
          initialValues={{ isActive: true, sortOrder: 100 }}>
          <Form.Item label="Thickness (mm)" name="thicknessMm" required
            rules={[
              { required: true, message: 'Thickness must be greater than zero.' },
              { type: 'number', min: 0.01, message: 'Thickness must be greater than zero.' }
            ]}>
            <InputNumber min={0.1} step={0.5} style={{ width: '100%' }}
              autoFocus placeholder="e.g., 12" />
          </Form.Item>
          <Form.Item label="Label" name="thicknessLabel"
            extra="Leave blank to use the default (e.g., '12mm').">
            <Input placeholder="12mm" />
          </Form.Item>
          <Form.Item label="Sort Order" name="sortOrder">
            <InputNumber min={0} step={10} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="Active" name="isActive" valuePropName="checked">
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>
          <Form.Item label="Remarks" name="remarks">
            <Input.TextArea rows={2} maxLength={500} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
